/**
 * Consolidates items that share the same rmsItemNumber by summing their quantities
 * and preserving per-unit pricing from the first occurrence.
 *
 * Used in Local Sourcing flows to prevent duplicate entries in price quote
 * and create agreement requests.
 */

export interface ConsolidatableItem {
  rmsItemNumber?: string;
  qty?: string;
  quantity?: string;
  rmsItemQuantity?: string;
  [key: string]: unknown;
}

/**
 * Groups items by rmsItemNumber and consolidates duplicates into a single entry
 * with summed quantity. Items without rmsItemNumber or with unique values pass
 * through unchanged.
 *
 * @param items - Array of items to consolidate
 * @returns Consolidated array with one entry per unique rmsItemNumber
 */
export function consolidateItemsByRmsNumber<T extends ConsolidatableItem>(
  items: T[]
): T[] {
  if (!items || items.length === 0) {
    return [];
  }

  // Group items by rmsItemNumber, preserving insertion order
  const { groupOrder, groups, noKeyItems } = groupItemsByRmsNumber(items);

  // Build consolidated result from groups
  const result: T[] = buildConsolidatedResult(groupOrder, groups);

  // Append items without rmsItemNumber at the end, unchanged
  return result.concat(noKeyItems);
}

/**
 * Groups items into a Map keyed by rmsItemNumber.
 * Items without a key are collected separately.
 */
function groupItemsByRmsNumber<T extends ConsolidatableItem>(items: T[]) {
  const groupOrder: string[] = [];
  const groups: Map<string, T[]> = new Map();
  const noKeyItems: T[] = [];

  for (const item of items) {
    const key = item.rmsItemNumber;

    if (!key) {
      noKeyItems.push(item);
      continue;
    }

    if (!groups.has(key)) {
      groupOrder.push(key);
      groups.set(key, []);
    }
    // Safe to access — key was just set above if it didn't exist
    const group = groups.get(key);
    if (group) {
      group.push(item);
    }
  }

  return { groupOrder, groups, noKeyItems };
}

/**
 * Builds the consolidated result array from grouped items.
 * Single-item groups pass through unchanged.
 * Multi-item groups are merged into one entry with summed quantity.
 */
function buildConsolidatedResult<T extends ConsolidatableItem>(
  groupOrder: string[],
  groups: Map<string, T[]>
): T[] {
  const result: T[] = [];

  for (const key of groupOrder) {
    const group = groups.get(key);
    if (!group) continue;

    if (group.length === 1) {
      // Single item — pass through unchanged
      result.push(group[0]);
    } else {
      // Multiple items — consolidate into one entry
      result.push(mergeGroup(group));
    }
  }

  return result;
}

/**
 * Merges a group of items with the same rmsItemNumber into a single entry.
 * Sums quantities and only updates quantity fields that exist on the input.
 */
function mergeGroup<T extends ConsolidatableItem>(group: T[]): T {
  const base = { ...group[0] };

  // Sum all individual quantities from the group entries
  const totalQty = group.reduce((sum, entry) => {
    const entryQty =
      entry.qty || entry.quantity || entry.rmsItemQuantity || '1';
    return sum + parseInt(entryQty, 10);
  }, 0);

  const qtyStr = String(totalQty);

  // Only update quantity fields that already exist on the input items
  if ('qty' in group[0]) {
    base.qty = qtyStr as T['qty'];
  }
  if ('rmsItemQuantity' in group[0]) {
    base.rmsItemQuantity = qtyStr as T['rmsItemQuantity'];
  }
  if ('quantity' in group[0]) {
    base.quantity = qtyStr as T['quantity'];
  }

  return base as T;
}
